import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getDocumentation, getCustomers, getProjects, deleteDocumentation } from '../services/api';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus, 
  Search, 
  Upload,
  Trash2,
  ArrowRight,
  Loader2,
  Filter,
  Building2,
  FolderOpen,
  Calendar,
  Send
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DocumentationPage = () => {
  const { canUpload, canDelete, canRequestDelete } = useAuth();
  const [documentation, setDocumentation] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, customersRes, projectsRes] = await Promise.all([
        getDocumentation(),
        getCustomers(),
        getProjects()
      ]);
      setDocumentation(docsRes.data);
      setCustomers(customersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load documentation');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (canDelete) {
      if (!window.confirm(`Delete documentation "${title}"?`)) return;
      try {
        await deleteDocumentation(id);
        toast.success('Documentation deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete documentation');
      }
    } else if (canRequestDelete) {
      const reason = window.prompt(`Request deletion of "${title}"?\nEnter a reason (optional):`);
      if (reason === null) return; // cancelled
      try {
        await axios.post(`${API_URL}/delete-requests`, { doc_id: id, reason });
        toast.success('Delete request submitted for admin approval');
      } catch (error) {
        const msg = error.response?.data?.detail || 'Failed to submit delete request';
        toast.error(msg);
      }
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown';
  };

  const getCustomerName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return 'Unknown';
    const customer = customers.find(c => c.id === project.customer_id);
    return customer?.name || 'Unknown';
  };

  const filteredDocs = documentation.filter(doc => {
    const matchesSearch = 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.ai_summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const project = projects.find(p => p.id === doc.project_id);
    const matchesCustomer = filterCustomer === 'all' || project?.customer_id === filterCustomer;
    const matchesProject = filterProject === 'all' || doc.project_id === filterProject;
    const matchesType = filterType === 'all' || doc.file_type === filterType;
    
    return matchesSearch && matchesCustomer && matchesProject && matchesType;
  });

  const fileTypes = [...new Set(documentation.map(d => d.file_type))].filter(Boolean);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="documentation-title">
              Documentation
            </h1>
            <p className="text-slate-500 mt-1">Browse all AI-generated documentation</p>
          </div>
          {canUpload && (
            <Link to="/documentation/upload">
              <Button className="bg-[#2B8FD8] hover:bg-[#2075b3]" data-testid="upload-docs-btn">
                <Upload className="h-4 w-4 mr-2" />
                Upload New
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search documentation..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-docs-input"
            />
          </div>
          <Select value={filterCustomer} onValueChange={setFilterCustomer}>
            <SelectTrigger className="w-[180px]">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-[180px]">
              <FolderOpen className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects
                .filter(p => filterCustomer === 'all' || p.customer_id === filterCustomer)
                .map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[150px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {fileTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Documentation Grid */}
        {filteredDocs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocs.map((doc) => (
              <Card 
                key={doc.id} 
                className="border-slate-200 hover:shadow-md transition-all hover:border-blue-200 group"
                data-testid={`doc-card-${doc.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-50 rounded-lg">
                        <FileText className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-slate-900 font-['Plus_Jakarta_Sans'] line-clamp-1">
                          {doc.title}
                        </CardTitle>
                        <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
                          <span>{getProjectName(doc.project_id)}</span>
                          <span className="text-slate-300">•</span>
                          <Badge variant="secondary" className="text-xs px-1.5 py-0">
                            v{doc.version}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    {(canDelete || canRequestDelete) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`opacity-0 group-hover:opacity-100 ${
                          canDelete 
                            ? 'text-slate-400 hover:text-red-500' 
                            : 'text-slate-400 hover:text-amber-500'
                        }`}
                        onClick={(e) => { e.preventDefault(); handleDelete(doc.id, doc.title); }}
                        title={canDelete ? 'Delete' : 'Request deletion'}
                      >
                        {canDelete ? <Trash2 className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 mb-1 flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {getCustomerName(doc.project_id)}
                  </p>
                  {doc.ai_summary && (
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{doc.ai_summary}</p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    <Badge variant="outline" className="text-xs">
                      {doc.file_type}
                    </Badge>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(doc.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {doc.tags && doc.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {doc.tags.slice(0, 3).map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-slate-100 text-slate-600 text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <Link to={`/projects/${doc.project_id}`}>
                    <Button variant="outline" size="sm" className="w-full group-hover:border-[#2B8FD8] group-hover:text-[#2B8FD8]">
                      View Documentation <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200 border-dashed">
            <CardContent className="py-16 text-center">
              <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Plus_Jakarta_Sans']">
                {searchQuery || filterCustomer !== 'all' || filterProject !== 'all' 
                  ? 'No documentation found' 
                  : 'No documentation yet'}
              </h3>
              <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                {searchQuery || filterCustomer !== 'all' || filterProject !== 'all'
                  ? 'Try adjusting your search or filters' 
                  : 'Upload integration files to generate AI-powered documentation'}
              </p>
              {!searchQuery && filterCustomer === 'all' && filterProject === 'all' && canUpload && (
                <Link to="/documentation/upload">
                  <Button className="bg-[#2B8FD8] hover:bg-[#2075b3]">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Your First File
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default DocumentationPage;
